import { Component, Inject, Input, OnDestroy, OnInit, computed } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ActionResponse, ActionType, ActionTypeValues, CU, ChapterType, IInfoChapter, IPaymentAuth, IPaymentAuthRedirect, IUser, ICreditCardDTO, PlatformType, sellaErrorKey } from 'common_library';
import { CdcWaitingPhase } from './components/cdc-waiting/cdc-waiting.component';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from 'src/app/services/entities/user.service';
import { ToastService } from 'src/app/services/utils/toast.service';
import { MasterDetailShowType } from '../master-detail/master-detail.component';
import { RxState } from '@rx-angular/state';
import { BehaviorSubject, Subscription, filter, map } from 'rxjs';
import { GLOBAL_RX_STATE, GlobalState, Nullable } from 'src/app/app-state.tokens';
import { HttpIoService } from 'src/app/services/communication/http-io.service';
import { ActionExecutorService } from 'src/app/services/entities/action-executor.service';
import { BrowserNativeService } from 'src/app/services/native/browser-native.service';
import { embeddedThreeDsUrl } from './components/cdc-3ds/cdc-3ds.component';
import { InfoService } from 'src/app/services/entities/info.service';
import { TranslateService } from '@ngx-translate/core';
import { MainStateService } from 'src/app/services/state/app-main-state.service';

export interface IResValidationCard { type: 'success' | 'failed', data: any }

interface StepItem {
  id: number,
  title?: string;
  subtitle?: string;
  icon?: string,
  chapterId: string,
  previuosStepsAccepted: boolean;
  mustBeAccetped: boolean,
  accepted: boolean,
  sections: StepSectionItem[],
  stepFromCms: boolean,
  type: string
}

interface StepSectionItem {
  title?: string;
  subtitle?: string;
  icon?: string,
  html?: string[] | null
}

interface ILocalState {
  paymentAuth: Nullable<Partial<IPaymentAuth>>,
  waiting: boolean,
  waitingPhase: CdcWaitingPhase
}


@Component({
  selector: 'yf-cdc-auth',
  templateUrl: './cdc-auth.component.html',
  styleUrls: ['./cdc-auth.component.scss'],
  providers: [RxState],
  standalone: false
})
export class CdcAuthComponent implements OnInit, OnDestroy {
  chapters$ = new BehaviorSubject<IInfoChapter[]>([]);
  stepItems$ = new BehaviorSubject<StepItem[]>([]);
  currentStepDetail$ = new BehaviorSubject<StepItem>(null);
  currentStepDetailAccepted$ = new BehaviorSubject<boolean>(false);
  loadingNextStep$ = new BehaviorSubject<boolean>(false);
  compactMode: boolean;
  user: IUser;
  CU = CU
  @Input() resValidationCard: IResValidationCard;

  readonly paymentAuth$ = this.localState.select("paymentAuth");
  $sessionInProgress = computed(() => this.mainStateService.$sessions()?.length > 0); // Right operand of ?? is unreachable because the left operand is never nullish
  masterOrDetail: MasterDetailShowType = 'detail';
  readonly waiting$ = this.localState.select("waiting");
  readonly waitingPhase$ = this.localState.select("waitingPhase");
  pendingSuccessActionUid: string | null = null;
  /** Action di FALLIMENTO: serve a sapere PERCHÉ la banca ha rifiutato. */
  pendingFailedActionUid: string | null = null;
  /** URL 3DS incastonato: valorizzato = il frame della banca è a schermo. */
  threeDsUrl: string | null = null;
  /** Lo stesso link come arriva dal backend, per la via di fuga nel browser. */
  private threeDsHref: string | null = null;
  /** Un solo esito per registrazione: frame e chiusura possono arrivare insieme. */
  private threeDsConcluded = false;
  /** Una risoluzione alla volta: il frame naviga più volte durante la challenge. */
  private threeDsResolving = false;
  /**
   * Id della carta attiva PRIMA di questo 3DS (null se non ce n'era).
   * Serve a distinguere "la nuova registrazione e' arrivata" da "esiste una
   * carta": senza il confronto, chi sostituisce una carta si vedrebbe
   * confermare quella VECCHIA. Prima qui c'era un booleano che, se una carta
   * c'era, spegneva la risoluzione per sempre — e lasciava l'utente appeso.
   */
  private paymentAuthIdBeforeSubmit: string | null = null;
  /**
   * L'esito del 3DS lo scrive il BACKEND quando arriva la notify S2S del PSP.
   * Non possiamo dipendere dagli eventi `load` di un iframe cross-origin: con
   * una carta frictionless la banca autorizza subito, il frame fa il suo ultimo
   * load e noi chiediamo l'esito un istante PRIMA che la notify atterri —
   * risposta "nessuna carta", e nessuno guardava piu'. Si interroga a intervalli
   * finche' l'esito non c'e'.
   */
  private threeDsPoll: ReturnType<typeof setInterval> | null = null;
  private visibilitySub: Subscription | null = null;
  private threeDsPollAttempts = 0;
  /**
   * ~3 minuti. L'intervallo e' corto apposta: sulla carta frictionless l'esito
   * e' pronto quasi subito e il pannello del browser va chiuso in fretta, se no
   * l'utente vede aprirsi una pagina che non gli serve e resta li' a guardarla.
   */
  private static readonly THREE_DS_POLL_MS = 1200;
  private static readonly THREE_DS_POLL_MAX = 150;

  constructor(
    @Inject(GLOBAL_RX_STATE) private globalState: RxState<GlobalState>,
    private localState: RxState<ILocalState>,
    private toastService: ToastService,
    private modalCtrl: ModalController,
    private userService: UserService,
    private alertCtrl: AlertController,
    private HIO: HttpIoService,
    private actionExecutor: ActionExecutorService,
    private mainStateService: MainStateService,
    private infoService: InfoService,
    private translate: TranslateService,
    private browserNative: BrowserNativeService
  ) {
    this.localState.set({ waiting: true, waitingPhase: 'loading' });
  }
  async ngOnInit() {
    const paymentAuth = await this.userService.getMyPaymentAuth();
    this.localState.set({ waiting: false, paymentAuth });
    this.user = this.mainStateService.getUser();

    await this.populateStepItems();

    if (this.resValidationCard) {
      this.showStepItemByType("CREDIT_CARD");
      if (this.resValidationCard.type === 'success') {
        const alertCartConvalidated = await this.alertCtrl.create({
          header: this.translate.instant("TOAST_MESSAGES.OPERATION_SUCCESSFULL"),
          message: this.translate.instant("CDC_AUTHENTICATION.CARD_SUCCESSFULLY_CONVALIDATED"),
          buttons: [{ text: this.translate.instant("BUTTONS.CLOSE") }]
        })
        await alertCartConvalidated.present();
      }
      if (this.resValidationCard.type === 'failed') {
        const alertCartConvalidated = await this.alertCtrl.create({
          header: this.translate.instant("TOAST_MESSAGES.OPERATION_FAILED"),
          message: this.sellaErrorMessage(this.resValidationCard?.data?.errorCode, this.translate.instant("CDC_AUTHENTICATION.CARD_CONVALIDATION_FAILED")),
          buttons: [{ text: this.translate.instant("BUTTONS.CLOSE") }],
          cssClass: 'card-auth-failed-alert',
        })
        await alertCartConvalidated.present();
      }
    }
    else {
      if (this.userHasAcceptedTerms()) {
        this.showStepItemByType("CREDIT_CARD")
      }
      else {
        this.showStepItem(this.stepItems$.getValue()[0]);
      }
    }
  }

  showStepItemByType(type: string) {
    let typeStep = this.stepItems$.getValue().find(c => c.type === type);
    this.showStepItem(typeStep);
  }

  async populateStepItems(forceRefreshContent: boolean = false) {
    const cmsContent = this.mainStateService.getCmsContent();
    let stepsChapters: StepItem[] = [];
    var currentIndex = 0;
    let mustRefreshCmsContent = false;

    // ISTRUZIONI
    //let instructionChapter = res.infoChapters.find(c => c.type === ChapterType.PAYMENT_INSTRUCTIONS);
    let instructionChapter = cmsContent.infoChapters.find(c => c.type === ChapterType.PAYMENT_INSTRUCTIONS);
    if (instructionChapter) {
      currentIndex++;
      let instructionItem = this.mapInfoChapterToStepItem(instructionChapter, currentIndex);
      if (instructionItem.mustBeAccetped) {
        // check per verificare se l'utente ha mai accettato in passato tale step
        const rv = await this.userService.checkUserChapterAcceptance(instructionChapter.id);
        if (rv) {
          instructionItem.accepted = true;
        }
        else {
          if (!forceRefreshContent) {
            mustRefreshCmsContent = true;
          }
        }
      }
      instructionItem.previuosStepsAccepted = true;
      stepsChapters.push(instructionItem);
    }

    // TERMINI E CONDIZIONI
    let termsChapter = cmsContent.infoChapters.find(c => c.type === ChapterType.TERMS_AND_CONDITIONS);
    if (termsChapter) {
      currentIndex++;
      let termsItem = this.mapInfoChapterToStepItem(termsChapter, currentIndex);
      if (termsItem.mustBeAccetped) {
        const rv = await this.userService.checkUserChapterAcceptance(termsChapter.id);
        if (rv) {
          termsItem.accepted = true;
        }
        else {
          if (!forceRefreshContent) {
            mustRefreshCmsContent = true;
          }
        }
      }
      termsItem.previuosStepsAccepted = stepsChapters.filter(c => c.mustBeAccetped && !c.accepted).length === 0;
      stepsChapters.push(termsItem);

      if (mustRefreshCmsContent) {
        await this.infoService.getCmsContent();
        this.populateStepItems(true);
      }
    }

    // CARTA DI CREDITO: STEP SCOLPITO
    currentIndex++;
    let creditCardItem: StepItem = {
      chapterId: null,
      accepted: true,
      mustBeAccetped: true,
      id: currentIndex,
      previuosStepsAccepted: stepsChapters.filter(c => c.mustBeAccetped && !c.accepted).length === 0,
      sections: [],
      stepFromCms: false,
      type: "CREDIT_CARD",
      title: this.translate.instant("CDC_AUTHENTICATION.DEBIT_AUTHORIZATION"),
      icon: "card-outline"
    }
    stepsChapters.push(creditCardItem);

    this.stepItems$.next(stepsChapters);
  }

  showStepItem(item: StepItem) {
    this.currentStepDetailAccepted$.next(item?.accepted ? true : false);
    this.masterOrDetail = !!item ? 'detail' : 'master';
    this.currentStepDetail$.next(item);
  }

  async showNextStepItem(item: StepItem) {
    this.loadingNextStep$.next(true);
    let canGoNextStep = false;
    if (item.mustBeAccetped) {
      if (!item.accepted) {
        if (this.currentStepDetailAccepted$.getValue()) {
          const rv = await this.userService.createUserChapterAcceptance(this.user.id, item.chapterId);
          if (rv) {
            this.populateStepItems();
            canGoNextStep = true;
          }
        }
      }
      else { canGoNextStep = true; }
    }
    else {
      canGoNextStep = true;
    }

    if (canGoNextStep) {
      let nextStep = this.stepItems$.getValue().find(c => c.id === item.id + 1);
      if (nextStep) {
        this.showStepItem(nextStep);
      }
      this.loadingNextStep$.next(false);
    }
  }

  toggleAcceptStepItem() {
    this.currentStepDetailAccepted$.next(!this.currentStepDetailAccepted$.getValue());
  }

  showWaiting() {
    this.masterOrDetail = 'master';
    this.localState.set({ waiting: true, waitingPhase: 'bank' });
  }

  compactModeChange(compactMode: boolean) {
    this.compactMode = compactMode;
  }

  async cdcRevocation() {
    if (!this.mainStateService.getSessions() || this.mainStateService.getSessions().length === 0) {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant("CDC_AUTHENTICATION.REVOKE_AUTHORIZATION"),
        message: this.translate.instant("CDC_AUTHENTICATION.REVOKE_AUTHORIZATION_MESSAGE"),
        buttons: [
          {
            text: this.translate.instant("BUTTONS.CANCEL"),
            role: 'cancel',
          },
          {
            text: this.translate.instant("BUTTONS.CONFIRM"),
            role: 'confirm',
            handler: async () => {
              await this.cdcRevocarionConfirmed();
            },
            cssClass: 'alert-button-red'
          }
        ],
      })
      await alert.present();
    }
  }

  private async cdcRevocarionConfirmed() {
    try {
      const result = await this.userService.removeMyPaymentAuth();
      if (result) {
        this.localState.set({ paymentAuth: null });
        await this.toastService.presentBasicToast({ icon: "close-circle-outline", header: this.translate.instant("TOAST_MESSAGES.OPERATION_SUCCESSFULL"), message: this.translate.instant("CDC_AUTHENTICATION.REVOKE_AUTHORIZATION_SUCCESSFULL"), color: "secondary" });
      } else {
        await this.toastService.presentBasicToast({ icon: "warning-outline", header: this.translate.instant("TOAST_MESSAGES.WARNING"), message: this.translate.instant("CDC_AUTHENTICATION.CANNOT_REVOKE_AUTHORIZATION"), color: 'danger' });
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.message) {
        await this.toastService.presentBasicToast({ icon: "warning-outline", header: this.translate.instant("TOAST_MESSAGES.WARNING"), message: error.message, color: 'danger' });
      }
    }
  }

  /**
   * "Registra con Google Pay" — visibile solo dove Google Pay esiste davvero
   * (web e Android). MAI su iOS: la pagina hosted lì proporrebbe Apple Pay, che
   * NON è registrabile (token di dispositivo: gli addebiti ricorrenti vengono
   * declinati) — meglio nessuna promessa che un errore dopo il pagamento.
   * Il gate d'audience è lo stesso del resto (il server ricontrolla comunque).
   */
  googlePayRegistrationVisible(): boolean {
    if (!this.mainStateService.getUser()?.hostedPayments) return false;
    const platform = this.mainStateService.getPlatformType();
    return platform !== PlatformType.iOSNative && platform !== PlatformType.iOSMobileWeb;
  }

  /**
   * Registrazione dalla pagina hosted di Axerve: l'utente sceglie Google Pay
   * (o una carta) lì sopra. Su nativo la pagina vive nel browser di sistema
   * (i wallet non funzionano in un iframe); su web si esce e si rientra dal
   * redirect con l'action uid, stessa strada della carta classica.
   */
  async registerWithGooglePay(): Promise<void> {
    if (!this.userHasAcceptedTerms()) {
      await this.toastService.presentBasicToast({ icon: "warning", header: this.translate.instant("TOAST_MESSAGES.WARNING"), message: this.translate.instant("CDC_AUTHENTICATION.ACCEPT_TERMS_MESSAGE"), color: 'warning' });
      this.showStepItemByType(ChapterType.TERMS_AND_CONDITIONS);
      return;
    }
    this.showStepItem(null);
    this.localState.set({ waiting: true, waitingPhase: 'submitting' });
    let rv: IPaymentAuthRedirect = null;
    try {
      rv = await this.userService.addMeCreditCardHosted();
    } catch (error) {
      const backendMessage = error instanceof HttpErrorResponse ? (error.error?.message ?? null) : null;
      await this.hostedRegistrationFailed(backendMessage);
      return;
    }
    this.pendingSuccessActionUid = rv?.successActionUid ?? null;
    this.pendingFailedActionUid = rv?.failedActionUid ?? null;
    if (!rv?.href) {
      await this.hostedRegistrationFailed(null);
      return;
    }
    this.localState.set({ waitingPhase: 'bank' });
    if (!this.mainStateService.getNative()) {
      window.location.href = rv.href;
      return;
    }
    // Stessa rete del 3DS: si chiede l'esito al backend mentre l'utente e' sulla
    // pagina del PSP, e si ricontrolla appena rientra in primo piano. Senza,
    // l'esito si risolveva UNA volta sola alla chiusura del browser — e chi
    // chiudeva in fretta si vedeva dire "non salvato" con la carta registrata.
    this.threeDsConcluded = false;
    this.paymentAuthIdBeforeSubmit = this.localState.get('paymentAuth')?.id ?? null;
    this.startThreeDsPolling();
    try {
      await this.browserNative.openAndWaitClose(rv.href);
    } catch (err) {
      console.log('🐱️ : apertura browser di sistema non riuscita', err);
    }
    if (this.threeDsConcluded) return; // il polling ha gia' concluso
    this.stopThreeDsPolling();
    await this.onHostedRegistrationClosed();
  }

  /**
   * Rientro dalla pagina hosted (nativo): l'esito lo dice il backend eseguendo
   * l'action di successo — recovery A3, idempotente — MAI la chiusura del
   * browser. Se non c'è un metodo salvato, lo si dice chiaro: un metodo che
   * sembra salvato e non lo è fallirebbe alla prima ricarica da pagare.
   */
  private async onHostedRegistrationClosed(): Promise<void> {
    const outcome = await this.resolveHostedRegistrationOutcome();
    if (outcome === 'success') {
      // stessa conclusione della carta classica: recap con la conferma
      this.modalCtrl.dismiss();
      await this.creditCardDataPresent({ type: 'success', data: {} });
      return;
    }
    const key = outcome === 'apple-pay'
      ? 'CDC_AUTHENTICATION.HOSTED_REGISTRATION.APPLE_PAY_NOT_SAVABLE'
      : 'CDC_AUTHENTICATION.HOSTED_REGISTRATION.NOT_SAVED';
    await this.hostedRegistrationFailed(this.translate.instant(key));
  }

  /**
   * Esito della registrazione hosted, chiesto al backend (retry dell'action di
   * successo, idempotente). `apple-pay` = l'utente ha pagato con Apple Pay
   * sulla pagina: il backend NON salva niente (token di dispositivo) e va
   * spiegato con parole sue, non con un generico "riprova".
   */
  private async resolveHostedRegistrationOutcome(): Promise<'success' | 'apple-pay' | 'failed'> {
    if (!this.pendingSuccessActionUid) return 'failed';
    try {
      const rv = await this.actionExecutor.execute(this.pendingSuccessActionUid);
      if (rv?.data && !rv.err && rv.type === ActionType.CdCAuthSuccess) return 'success';
      if (String(rv?.err ?? '').includes('APPLE_PAY_NON_REGISTRABILE')) return 'apple-pay';
      // SOLO su "già eseguita": vuol dire che QUESTA action l'ha completata la
      // notify S2S vincendo la corsa, e il suo successo ha salvato la carta —
      // confermato guardando lo stato reale. Su ogni altro errore NON si guarda
      // il metodo salvato: un utente che aveva già una carta e la cui
      // registrazione è fallita si vedrebbe dire "successo" per la carta VECCHIA.
      if (String(rv?.err ?? '').toLowerCase().includes('già eseguita')) {
        const paymentAuth = await this.userService.getMyPaymentAuth();
        if (paymentAuth) return 'success';
      }
    } catch (err) {
      console.log('🐱️ : esito registrazione hosted non recuperabile', err);
    }
    return 'failed';
  }

  /** Niente è stato salvato: si torna al form e lo si dice. `null` = messaggio generico. */
  private async hostedRegistrationFailed(message: string | null): Promise<void> {
    this.localState.set({ waiting: false });
    this.showStepItemByType("CREDIT_CARD");
    await this.toastService.presentBasicToast({
      header: this.translate.instant("TOAST_MESSAGES.OPERATION_FAILED"),
      message: message || this.translate.instant("CDC_AUTHENTICATION.HOSTED_REGISTRATION.NOT_SAVED"),
      color: 'danger',
      icon: 'warning-outline'
    });
  }

  async cdcSubmitted(cdc: ICreditCardDTO) {

    if (!this.userHasAcceptedTerms()) {
      await this.toastService.presentBasicToast({ icon: "warning", header: this.translate.instant("TOAST_MESSAGES.WARNING"), message: this.translate.instant("CDC_AUTHENTICATION.ACCEPT_TERMS_MESSAGE"), color: 'warning' });
      this.showStepItemByType(ChapterType.TERMS_AND_CONDITIONS);
    }
    else {
      this.showStepItem(null);
      this.localState.set({ waiting: true, waitingPhase: 'submitting' });
      let rv: IPaymentAuthRedirect = null;
      try {
        rv = await this.userService.addMeCreditCard(cdc);
      } catch (error) {
        const backendMessage = error instanceof HttpErrorResponse ? (error.error?.message ?? null) : null;
        const sellaCode = error instanceof HttpErrorResponse ? (error.error?.sellaErrorCode ?? null) : null;
        await this.toastService.presentBasicToast({
          header: this.translate.instant("TOAST_MESSAGES.OPERATION_FAILED"),
          message: this.sellaErrorMessage(sellaCode, backendMessage || this.translate.instant("CDC_AUTHENTICATION.CARD_CONVALIDATION_FAILED")),
          color: 'danger',
          icon: 'warning-outline'
        });
        this.showStepItemByType("CREDIT_CARD");
        this.localState.set({ waiting: false });
        return;
      }
      this.pendingSuccessActionUid = rv?.successActionUid ?? null;
      this.pendingFailedActionUid = rv?.failedActionUid ?? null;
      //TODO pensare se è giusto non far sparire il waitingToast
      // this.appService.hideWaitingToast();
      // FRICTIONLESS: la banca ha gia' autorizzato al submit. Non c'e' nessuna
      // pagina da mostrare — aprirla comunque significava far comparire e
      // sparire un browser su un giro gia' concluso. Si aspetta solo che la
      // riga compaia (la persistenza e' gia' partita lato backend).
      if (rv?.alreadyAuthorized) {
        this.threeDsConcluded = false;
        this.paymentAuthIdBeforeSubmit = this.localState.get('paymentAuth')?.id ?? null;
        this.localState.set({ waitingPhase: 'bank' });
        this.startThreeDsPolling();
        return;
      }
      if (rv?.href) {
        if (this.mainStateService.getNative()) {
          // 3DS A PAGINA INTERA nel browser di sistema (il pannello resta sopra
          // l'app: l'utente non "esce"). NON e' una resa: incastonarlo non puo'
          // funzionare su telefono, perche' la misura del contenuto la decide
          // `challengeWindowSize` dello standard EMV 3DS (02 = 390x400) e l'ACS
          // compone dentro quella scatola qualunque dimensione abbia il nostro
          // frame. Axerve non espone quel campo, e la sua integrazione lightbox
          // ufficiale su mobile REDIRIGE a pagina intera invece di incastonare.
          // L'esito non dipende dal rientro: lo chiede il backend (polling +
          // ricontrollo al ritorno in primo piano).
          this.threeDsConcluded = false;
          this.paymentAuthIdBeforeSubmit = this.localState.get('paymentAuth')?.id ?? null;
          this.localState.set({ waitingPhase: 'bank' });
          this.threeDsHref = rv.href;
          this.startThreeDsPolling();
          await this.openThreeDsFullScreen(rv.href);
        } else {
          this.localState.set({ waitingPhase: 'bank' });
          window.location.href = rv.href
        }
      } else {
        this.toastService.presentBasicToast({
          header: this.translate.instant("TOAST_MESSAGES.OPERATION_FAILED"),
          message: this.translate.instant("CDC_AUTHENTICATION.CARD_CONVALIDATION_FAILED") + ' ' + this.translate.instant("CDC_AUTHENTICATION.CARD_DEBIT_NOT_ALLOWED"),
          color: 'danger',
        });
        this.showStepItemByType("CREDIT_CARD");
        this.localState.set({ waiting: false });
      }
    }
  }

  /**
   * Apre il 3DS a pagina intera e aspetta che l'utente chiuda. Se il browser di
   * sistema non si apre (plugin assente, WebView senza handler), si ripiega
   * sull'iframe: stretto, ma meglio di un vicolo cieco.
   */
  private async openThreeDsFullScreen(href: string): Promise<void> {
    try {
      await this.browserNative.openAndWaitClose(href);
    } catch (err) {
      console.log('🐱️ : browser di sistema non disponibile, ripiego sull iframe', err);
      this.threeDsUrl = embeddedThreeDsUrl(href);
      return;
    }
    if (this.threeDsConcluded) return; // il polling ha gia' concluso mentre era fuori
    await this.on3dsClosed();
  }

  /**
   * Il frame 3DS ha navigato (challenge, ritorno, redirect intermedi). NON si
   * legge il suo URL — è cross-origin e comunque non sarebbe una prova: si
   * guarda se il backend ha registrato la carta. Nel percorso normale ci arriva
   * da solo, con la notify S2S del PSP (canale d'esito primario), quindi qui
   * basta una lettura: niente esecuzione di action a ogni navigazione, che
   * andrebbe in corsa proprio con la notify.
   */
  async on3dsFrameLoad(): Promise<void> {
    // Acceleratore, non innesco unico: il poller sotto copre il caso in cui
    // l'ultimo `load` arriva PRIMA della notify S2S (carta frictionless).
    await this.checkThreeDsOutcome();
  }

  /**
   * Chiede al backend se la registrazione e' arrivata. `true` = concluso.
   *
   * Il confronto e' sull'ID: trovare "una carta" non prova niente se l'utente
   * ne aveva gia' una — sarebbe la vecchia, e gli confermeremmo una
   * registrazione che non e' avvenuta.
   */
  private async checkThreeDsOutcome(): Promise<boolean> {
    if (this.threeDsConcluded || this.threeDsResolving) return this.threeDsConcluded;
    this.threeDsResolving = true;
    try {
      // L'esito lo DICE il backend: `PENDING | SUCCESS | FAILED`. Niente
      // deduzioni dalla comparsa di una carta, e soprattutto il rifiuto si sa
      // subito invece di scoprirlo chiudendo a mano.
      if (this.pendingSuccessActionUid) {
        const esito = await this.userService.getCdcRegistrationStatus(this.pendingSuccessActionUid);
        if (esito?.status === 'SUCCESS') {
          await this.conclude3ds({ type: 'success', data: {} });
          return true;
        }
        if (esito?.status === 'FAILED') {
          await this.concludeThreeDsFailure(esito.sellaErrorCode, esito.message);
          return true;
        }
        return false; // PENDING (o rete giu'): si continua ad aspettare
      }
      // Percorsi senza action uid: resta il confronto sull'id della carta.
      const paymentAuth = await this.userService.getMyPaymentAuth();
      if (!!paymentAuth?.id && paymentAuth.id !== this.paymentAuthIdBeforeSubmit) {
        await this.conclude3ds({ type: 'success', data: {} });
        return true;
      }
    } catch (err) {
      console.log('🐱️ : lettura esito 3DS non riuscita', err);
    } finally {
      this.threeDsResolving = false;
    }
    return false;
  }

  /**
   * La banca ha rifiutato: si chiude subito col motivo del PSP (mappato da C5),
   * senza far aspettare l'utente e senza costringerlo a chiudere per capire.
   */
  private async concludeThreeDsFailure(sellaCode?: string, backendMessage?: string): Promise<void> {
    if (this.threeDsConcluded) return;
    this.threeDsConcluded = true;
    this.stopThreeDsPolling();
    await this.browserNative.close(); // vale anche per il rifiuto: niente da fare li' sopra
    this.threeDsUrl = null;
    this.showStepItemByType('CREDIT_CARD');
    this.localState.set({ waiting: false });
    // "Pagato ma non registrabile" non e' un rifiuto della banca: all'utente
    // vanno dette le alternative, non un errore generico.
    const messaggio = sellaCode === 'APPLE_PAY_NON_REGISTRABILE'
      ? this.translate.instant('CDC_AUTHENTICATION.HOSTED_REGISTRATION.APPLE_PAY_NOT_SAVABLE')
      : sellaCode === 'TOKEN_NON_RIUTILIZZABILE'
        ? this.translate.instant('CDC_AUTHENTICATION.HOSTED_REGISTRATION.NOT_SAVED')
        : this.sellaErrorMessage(sellaCode, backendMessage || this.translate.instant('CDC_AUTHENTICATION.CARD_CONVALIDATION_FAILED'));
    await this.toastService.presentBasicToast({
      header: this.translate.instant('TOAST_MESSAGES.OPERATION_FAILED'),
      message: messaggio,
      color: 'danger',
      icon: 'warning-outline',
    });
  }

  /**
   * Interroga il backend finche' l'esito non c'e'. Scaduto il tetto si smette
   * di chiedere ma NON si dichiara fallita: l'utente ha ancora il frame davanti
   * e la chiusura fa il recovery completo (che sa anche dire PERCHE' e' fallita).
   */
  private startThreeDsPolling(): void {
    this.stopThreeDsPolling();
    this.threeDsPollAttempts = 0;
    // L'utente torna dal browser di sistema (o riapre l'app): e' il momento in
    // cui l'esito e' quasi certamente pronto, e su iOS i timer sono stati
    // congelati mentre era fuori. Si ricontrolla SUBITO invece di aspettare il
    // prossimo tick — cosi' il rientro non dipende dagli universal link, che
    // sul dominio corrente non sono configurati.
    this.visibilitySub?.unsubscribe();
    this.visibilitySub = this.globalState.select('visible')
      .pipe(filter((v: boolean) => !!v))
      .subscribe(() => { if (!this.threeDsConcluded) void this.checkThreeDsOutcome(); });
    this.threeDsPoll = setInterval(async () => {
      if (this.threeDsConcluded) { this.stopThreeDsPolling(); return; }
      if (++this.threeDsPollAttempts > CdcAuthComponent.THREE_DS_POLL_MAX) {
        console.log('🐱️ : 3DS senza esito entro il tempo massimo, resta la chiusura manuale');
        this.stopThreeDsPolling();
        return;
      }
      if (await this.checkThreeDsOutcome()) this.stopThreeDsPolling();
    }, CdcAuthComponent.THREE_DS_POLL_MS);
  }

  private stopThreeDsPolling(): void {
    if (this.threeDsPoll) { clearInterval(this.threeDsPoll); this.threeDsPoll = null; }
    this.visibilitySub?.unsubscribe();
    this.visibilitySub = null;
  }

  ngOnDestroy(): void {
    // Un interval lasciato vivo dopo la chiusura della modale continuerebbe a
    // interrogare il backend per minuti.
    this.stopThreeDsPolling();
  }

  /**
   * L'utente ha chiuso il 3DS (o il browser di sistema si è chiuso). Prima di
   * dire "annullato" si ritenta l'action di successo: è lo stesso recovery che
   * oggi recupera le registrazioni che altrimenti si perderebbero.
   */
  async on3dsClosed(): Promise<void> {
    if (this.threeDsConcluded) return;
    const recovered = await this.tryRecoverPendingAuth();
    if (recovered) return;
    this.threeDsConcluded = true;
    this.threeDsUrl = null;
    this.modalCtrl.dismiss();
    // La registrazione non è andata: si chiede al backend PERCHÉ, eseguendo
    // l'action di fallimento (che porta il codice errore del PSP, vedi C5).
    // Senza, una carta scaduta e una chiusura del browser darebbero lo stesso
    // messaggio — e all'utente diremmo "non confermata" invece di "carta scaduta".
    const motivo = await this.resolveFailureReason();
    this.toastService.presentBasicToast({
      header: this.translate.instant("TOAST_MESSAGES.WARNING"),
      message: motivo ?? this.translate.instant("CDC_AUTHENTICATION.THREE_DS.NOT_CONFIRMED"),
      color: 'danger',
      icon: 'warning'
    });
  }

  /**
   * Via di fuga: la pagina della banca non funziona nel frame (su iOS i cookie
   * di terze parti possono essere partizionati). Stessa pagina nel browser di
   * sistema — SFSafariViewController / Custom Tabs, l'utente non esce dall'app —
   * e alla chiusura si risolve l'esito come sempre. Nessuna injection di JS,
   * nessun URL sniffato: è la stessa rete del rientro hosted.
   */
  async on3dsOpenInBrowser(): Promise<void> {
    const url = this.threeDsHref; // fuori dal frame vale il link originale, non la variante lightbox
    if (!url) return;
    // Il poller NON si ferma: e' proprio qui che serve. Il rientro dal browser
    // richiederebbe un universal link sul dominio corrente (configurato solo per
    // app.yourfill.it), quindi la banca lascia l'utente nel browser. Continuando
    // a chiedere l'esito al backend, quando la carta viene registrata la modale
    // si risolve da sola e alla chiusura non c'e' nessun "errore" da mostrare.
    this.threeDsUrl = null;
    if (!this.threeDsPoll) this.startThreeDsPolling();
    try {
      await this.browserNative.openAndWaitClose(url);
    } catch (err) {
      console.log('🐱️ : apertura browser di sistema non riuscita', err);
    }
    await this.on3dsClosed();
  }

  /**
   * Esito acquisito: si chiude il frame e si mostra il recap. UNA VOLTA SOLA —
   * la lettura scatenata dal frame e la chiusura dell'utente possono arrivare
   * insieme, e due modali di successo per la stessa carta sono un bug che si
   * vede solo in produzione.
   */
  private async conclude3ds(outcome: IResValidationCard): Promise<void> {
    if (this.threeDsConcluded) return;
    this.threeDsConcluded = true;
    this.stopThreeDsPolling();
    // L'esito e' arrivato mentre l'utente poteva essere ancora sulla pagina del
    // PSP: il browser non torna all'app da solo, quindi lo chiudiamo noi.
    await this.browserNative.close();
    this.threeDsUrl = null;
    this.modalCtrl.dismiss();
    await this.creditCardDataPresent(outcome);
  }

  /**
   * Recovery del caso "3DS chiuso dopo l'autorizzazione": l'action di successo è
   * idempotente lato backend (marca executedAt solo dopo il salvataggio),
   * quindi si può ritentare: se la banca ha autorizzato, la carta viene
   * salvata ora; altrimenti il backend risponde err e non succede nulla.
   */
  /**
   * Perché la registrazione non è andata a buon fine. Esegue l'action di
   * fallimento, che il backend arricchisce col codice errore del PSP (C5): è la
   * stessa cosa che accadeva quando il redirect passava dalla `buyerKO`, solo
   * che ora ci arriviamo noi invece che il browser.
   *
   * Si chiama SOLO dopo che il recupero del successo è fallito: eseguirla prima
   * marcherebbe fallito un tentativo che magari stava per riuscire.
   *
   * `null` = motivo non determinabile, il chiamante usa il messaggio generico.
   */
  private async resolveFailureReason(): Promise<string | null> {
    if (!this.pendingFailedActionUid) return null;
    try {
      const rv = await this.actionExecutor.execute(this.pendingFailedActionUid);
      const codice = rv?.data?.errorCode;
      return codice ? this.sellaErrorMessage(codice) : null;
    } catch (err) {
      console.log('🐱️ : motivo del rifiuto non recuperabile', err);
      return null;
    }
  }

  private async tryRecoverPendingAuth(): Promise<boolean> {
    const outcome = await this.resolvePendingAuthOutcome();
    if (!outcome) return false;
    await this.conclude3ds(outcome);
    return true;
  }

  /** Messaggio specifico dal codice errore Sella; fallback al messaggio generico. */
  private sellaErrorMessage(code?: string | null, fallback?: string): string {
    const key = sellaErrorKey(code);
    if (key === 'GENERIC' && fallback) return fallback;
    return this.translate.instant(`CDC_AUTHENTICATION.ERRORS.${key}`);
  }

  /** L'esito vero, chiesto al backend. Nessun effetto sulla UI: decide chi chiama. */
  private async resolvePendingAuthOutcome(): Promise<IResValidationCard | null> {
    if (!this.pendingSuccessActionUid) return null;
    try {
      const rv = await this.actionExecutor.execute(this.pendingSuccessActionUid);
      if (rv?.data && !rv.err && rv.type === ActionType.CdCAuthSuccess) {
        return { type: 'success', data: rv.data };
      }
      // "già eseguita" può voler dire che la notify S2S ha vinto la corsa e la
      // carta è già salvata: si guarda lo stato reale prima di dire "annullato"
      const paymentAuth = await this.userService.getMyPaymentAuth();
      if (paymentAuth) return { type: 'success', data: {} };
    } catch (err) {
      console.log('🐱️ : recovery pending auth non riuscito', err);
    }
    return null;
  }

  async creditCardDataPresent(resValidationCard: { type: 'success' | 'failed', data: any } = null) {
    const modal = await this.modalCtrl.create({
      component: CdcAuthComponent,
      cssClass: 'yf-master-detail-modal',
      componentProps: { resValidationCard }
    });
    await modal.present();
  }

  mapInfoChapterToStepItem(chapter: IInfoChapter, index: number): StepItem {
    let stepItem: StepItem = {
      id: index,
      title: chapter.title,
      subtitle: chapter.subtitle,
      icon: chapter.icon,
      sections: this.populateStepSections(chapter),
      chapterId: chapter.id,
      mustBeAccetped: chapter.userAccepted,
      accepted: false,
      previuosStepsAccepted: false,
      stepFromCms: true,
      type: chapter.type
    }
    return stepItem;
  }

  private populateStepSections(chapters: any): StepSectionItem[] {
    const sections: any[] = chapters.infoSections;
    let sectionItems: StepSectionItem[] = [];
    for (let j = 0; j < sections.length; j++) {
      let stepSectionItem: StepSectionItem = {
        icon: sections[j]?.icon,
        html: sections[j]?.html,
        title: sections[j]?.title,
        subtitle: sections[j]?.subtitle
      };
      sectionItems.push(stepSectionItem);
    }
    return sectionItems;
  }

  userHasAcceptedTerms() {
    let termsStep = this.stepItems$.getValue().find(c => c.type === ChapterType.TERMS_AND_CONDITIONS);
    if (termsStep && termsStep.mustBeAccetped && !termsStep.accepted) {
      return false;
    }
    return true;
  }
}
